import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { deleteConnection, listConnections, listSystemConnections, testSystemConnection } from '@mkcore/platform/schema';
import { Badge, Button, EmptyState, ErrorState, PageHeader, Skeleton, TBody, THead, Table, Td, Th, Tr } from '@mkcore/ui';
import { call } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { useActionMutation, useErrorMessage } from '@/lib/mutation';
import { useCan } from '@/lib/session';
import { ConfirmButton } from '@/components/confirm';
import { McpServers } from '@/features/agents/mcp-servers';

export const Route = createFileRoute('/_app/admin/connections')({ component: Connections });

const tone = { active: 'success', pending: 'outline', error: 'warning', relink: 'danger' } as const;

const systemTone = { ok: 'success', error: 'danger', untested: 'outline', off: 'neutral' } as const;

/**
 * The connections of the app itself (CO-0026): configured or not, and a test that changes nothing, with the last
 * result. Testing needs admin:update.
 */
function SystemConnections() {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const canTest = useCan('admin:update');
  const q = useQuery({ queryKey: ['admin', 'systemConnection'], queryFn: () => call(listSystemConnections, {}) });
  const test = useActionMutation(testSystemConnection, { invalidate: [['admin', 'systemConnection']] });
  return (
    <section aria-labelledby="system-connections" className="mb-8 grid gap-2">
      <h2 id="system-connections" className="text-lg font-bold">
        {t('connections.systemTitle')}
      </h2>
      <p className="text-sm text-fg-2">{t('connections.systemHint')}</p>
      {q.isError ? (
        <ErrorState title={t('errors.generic')} description={message(q.error)} />
      ) : !q.data ? (
        <Skeleton className="h-40" />
      ) : (
        <Table>
          <THead>
            <Tr>
              <Th>{t('connections.connection')}</Th>
              <Th>{t('common.status')}</Th>
              <Th className="hidden md:table-cell">{t('connections.lastCheck')}</Th>
              <Th>{t('connections.detail')}</Th>
              {canTest && <Th className="text-right">{t('common.actions')}</Th>}
            </Tr>
          </THead>
          <TBody>
            {q.data.items.map((c) => (
              <Tr key={c.id}>
                <Td>
                  <span className="font-bold">{t(`connections.check.${c.id.replace('.', '_')}`, { defaultValue: c.id })}</span>
                  <span className="block text-xs text-fg-3">{c.system}</span>
                </Td>
                <Td>
                  <Badge tone={systemTone[c.status]}>{t(`connections.checkStatus.${c.status}`)}</Badge>
                </Td>
                <Td className="hidden md:table-cell tabular text-fg-2">{c.lastCheckedAt ? formatDateTime(c.lastCheckedAt) : '—'}</Td>
                <Td className="max-w-md text-xs">
                  <span className={c.status === 'error' ? 'text-danger' : 'text-fg-2'}>{c.status === 'off' ? c.configDetail : (c.detail ?? c.configDetail)}</span>
                  {c.status === 'ok' && c.lastError && c.lastErrorAt && <span className="block text-fg-3">{t('connections.lastErrorAt', { at: formatDateTime(c.lastErrorAt), error: c.lastError })}</span>}
                </Td>
                {canTest && (
                  <Td className="text-right">
                    {c.testable && c.configured && (
                      <Button
                        size="sm"
                        variant="secondary"
                        aria-label={t('connections.testCheck', { name: t(`connections.check.${c.id.replace('.', '_')}`, { defaultValue: c.id }) })}
                        loading={test.isPending && test.variables?.input.id === c.id}
                        onClick={() => test.mutate({ input: { id: c.id } })}
                      >
                        {t('connections.test')}
                      </Button>
                    )}
                  </Td>
                )}
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
    </section>
  );
}

/** Every person's links to external systems with their status, for support (CO-0026); never a token. */
function Connections() {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const canRemove = useCan('admin:update');
  const q = useQuery({ queryKey: ['admin', 'connection', 'all'], queryFn: () => call(listConnections, { scope: 'all' }) });
  const remove = useActionMutation(deleteConnection, { invalidate: [['admin', 'connection']], success: t('connections.removed') });
  if (q.isError) return <ErrorState title={t('errors.generic')} description={message(q.error)} />;
  return (
    <>
      <PageHeader eyebrow={t('nav.admin')} title={t('connections.adminTitle')} description={t('connections.adminHint')} />
      <SystemConnections />
      <McpServers />
      <h2 className="mb-2 text-lg font-bold">{t('connections.peopleTitle')}</h2>
      <ul className="mb-4 flex flex-wrap gap-2 text-sm">
        {(q.data?.providers ?? []).map((p) => (
          <li key={p.id}>
            <Badge tone={p.configured ? 'success' : 'outline'}>
              {p.title}: {p.configured ? t('connections.configured') : t('connections.notConfigured')}
            </Badge>
          </li>
        ))}
      </ul>
      {!q.data ? (
        <Skeleton className="h-48" />
      ) : !q.data.items.length ? (
        <EmptyState title={t('connections.none')} />
      ) : (
        <Table>
          <THead>
            <Tr>
              <Th>{t('connections.person')}</Th>
              <Th>{t('connections.system')}</Th>
              <Th>{t('connections.account')}</Th>
              <Th>{t('common.status')}</Th>
              <Th className="hidden md:table-cell">{t('connections.lastOkShort')}</Th>
              <Th className="hidden lg:table-cell">{t('connections.error')}</Th>
              {canRemove && <Th className="text-right">{t('common.actions')}</Th>}
            </Tr>
          </THead>
          <TBody>
            {q.data.items.map((c) => (
              <Tr key={c.id}>
                <Td className="font-bold">{c.userName ?? c.userId}</Td>
                <Td>{q.data.providers.find((p) => p.id === c.provider)?.title ?? c.provider}</Td>
                <Td>{c.accountLogin ?? '—'}</Td>
                <Td>
                  <Badge tone={tone[c.status]}>{t(`connections.status.${c.status}`)}</Badge>
                </Td>
                <Td className="hidden md:table-cell tabular text-fg-2">{c.lastOkAt ? formatDateTime(c.lastOkAt) : '—'}</Td>
                <Td className="hidden lg:table-cell max-w-xs truncate text-xs text-danger">{c.lastError ?? ''}</Td>
                {canRemove && (
                  <Td className="text-right">
                    <ConfirmButton
                      variant="ghost"
                      label={t('connections.remove')}
                      title={t('connections.removeFor', { name: c.userName ?? c.userId })}
                      description={t('connections.removeHint', { system: c.provider })}
                      confirmLabel={t('connections.remove')}
                      onConfirm={() => remove.mutateAsync({ input: { provider: c.provider, userId: c.userId } })}
                    />
                  </Td>
                )}
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
    </>
  );
}
